import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Card, Divider, Empty, Spin, Statistic, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { InfoCircleOutlined } from '@ant-design/icons';

const fmt = (num) => {
  if (num === null || num === undefined) return '-';
  const n = Number(num);
  if (Number.isNaN(n)) return '-';
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const SERIES_COLORS = {
  market: '#8ac9f5', // light blue for grid import
  pvUsage: '#f5af00', // solar consumption (yellow)
  pvExport: '#7f7f7f', // solar export (grey)
  transformer: '#d46b08', // transformer load line
};

const SummaryBox = ({ title, value, suffix }) => (
  <Card className="summaryBox">
    <Statistic title={title} value={fmt(value)} suffix={suffix} />
  </Card>
);

const UsageChart = ({
  loading,
  series = [],
  totals = {},
}) => {
  const hourlyData = useMemo(() => {
    if (!series || series.length === 0) return [];

    const buckets = {};
    for (let i = 0; i < 24; i++) {
      buckets[i] = { count: 0, gridImportKWh: 0, solarToSiteKWh: 0, gridExportKWh: 0, transformerLoading: 0, solarGenKWh: 0 };
    }

    series.forEach((item) => {
      // Backend timestamps are already aligned to HKT hour buckets; browser is also HKT, so use local hour directly.
      const h = dayjs(item.ts).hour();
      if (buckets[h]) {
        buckets[h].count++;
        buckets[h].gridImportKWh += Number(item.gridImportKWh || 0);
        buckets[h].solarToSiteKWh += Number(item.solarToSiteKWh || 0);
        buckets[h].gridExportKWh += Number(item.gridExportKWh || 0);
        buckets[h].transformerLoading += Number(item.transformerLoading || 0);
        buckets[h].solarGenKWh += Number(item.solarGenKWh || 0);
      }
    });

    const result = [];
    for (let i = 0; i < 24; i++) {
      const b = buckets[i];
      const count = b.count || 1;
      const hasData = b.count > 0;
      result.push({
        label: `${String(i).padStart(2, '0')}:00`,
        gridImportKWh: hasData ? b.gridImportKWh : null, // Sum for energy
        solarToSiteKWh: hasData ? b.solarToSiteKWh : null, // Sum for energy
        gridExportKWh: hasData ? b.gridExportKWh : null, // Sum for energy
        transformerLoading: hasData ? b.transformerLoading / count : null, // Average for loading
        solarGenKWh: hasData ? b.solarGenKWh : null, // Sum for energy
      });
    }
    return result;
  }, [series]);

  const chartOption = useMemo(() => {
    if (!hourlyData || hourlyData.length === 0) {
      return null;
    }
    const xLabels = hourlyData.map((item) => item.label);
    const toData = (key) => hourlyData.map((item) => item[key] || 0);

    const formatTooltip = (params) => {
      if (!params || !params.length) return '';

      const lines = [params[0].axisValue];
      params.forEach((p) => {
        const numericVal = Number(p.data);
        const isTransformer = p.seriesName === 'Transformer Load';
        const displayVal = Number.isNaN(numericVal)
          ? '-'
          : numericVal.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: isTransformer ? 2 : 0,
          });
        const suffix = isTransformer ? '%' : ' kWh';
        lines.push(`${p.marker} ${p.seriesName}: ${displayVal}${suffix}`);
      });

      return lines.join('<br/>');
    };

    return {
      color: [SERIES_COLORS.market, SERIES_COLORS.pvUsage, SERIES_COLORS.pvExport, SERIES_COLORS.transformer],
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: formatTooltip,
      },
      legend: {
        data: ['Market Usage', 'PV Export', 'PV Usage', 'Transformer Load'],
        bottom: 0,
        icon: 'rect',
        itemHeight: 2,
        itemWidth: 15,
        itemGap: 20,
      },
      grid: {
        left: 50,
        right: 50,
        bottom: 60,
        top: 30,
      },
      xAxis: {
        type: 'category',
        data: xLabels,
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
      },
      yAxis: [
        {
          type: 'value',
          name: 'kWh',
          position: 'left',
          splitLine: {
            lineStyle: { type: 'dashed', color: '#E8E8E8' }
          },
          axisLine: { show: false },
          axisTick: { show: false },
        },
        {
          type: 'value',
          name: '%',
          position: 'right',
          min: 0,
          max: 100,
          axisLabel: { formatter: '{value}%' },
          splitLine: { show: false },
          axisLine: { show: false },
          axisTick: { show: false },
        }
      ],
      series: [
        {
          name: 'Market Usage',
          type: 'line',
          smooth: false,
          stack: 'energy',
          data: toData('gridImportKWh'),
          lineStyle: { color: SERIES_COLORS.market, width: 1 },
          itemStyle: { color: SERIES_COLORS.market },
          areaStyle: { color: SERIES_COLORS.market, opacity: 0.6 },
          showSymbol: false,
          emphasis: { focus: 'series' },
        },
        {
          name: 'PV Export',
          type: 'line',
          smooth: false,
          stack: 'energy',
          data: toData('gridExportKWh'),
          lineStyle: { color: SERIES_COLORS.pvExport, width: 1 },
          itemStyle: { color: SERIES_COLORS.pvExport },
          areaStyle: { color: SERIES_COLORS.pvExport, opacity: 0.7 },
          showSymbol: false,
          emphasis: { focus: 'series' },
        },
        {
          name: 'PV Usage',
          type: 'line',
          smooth: false,
          stack: 'energy',
          data: toData('solarToSiteKWh'),
          lineStyle: { color: SERIES_COLORS.pvUsage, width: 1 },
          itemStyle: { color: SERIES_COLORS.pvUsage },
          areaStyle: { color: SERIES_COLORS.pvUsage, opacity: 0.8 },
          showSymbol: false,
          emphasis: { focus: 'series' },
        },
        {
          name: 'Transformer Load',
          type: 'line',
          smooth: false,
          yAxisIndex: 1,
          data: hourlyData.map((item) => {
            const val = item.transformerLoading;
            return val != null ? (Number(val) * 100).toFixed(2) : 0;
          }),
          lineStyle: { color: SERIES_COLORS.transformer, width: 2 },
          itemStyle: { color: SERIES_COLORS.transformer },
          showSymbol: false,
        },
      ],
    };
  }, [hourlyData]);

  const loadingOption = {
    color: '#00B855',
  };

  const emptyOption = {
    xAxis: { type: 'category', data: [] },
    yAxis: { type: 'value' },
    series: [],
  };



  return (
    <div className="usageChart">
      <div className="chart">
        <div className="summaryBoxes">
          <SummaryBox title="Market Usage" value={totals.gridImportKWh} suffix="kWh" />
          <Divider type="vertical" />
          <SummaryBox title="PV Export" value={totals.gridExportKWh} suffix="kWh" />
          <Divider type="vertical" />
          <SummaryBox title="Solar Generated" value={totals.solarGenKWh} suffix="kWh" />
          <Divider type="vertical" />
          <SummaryBox title="PV Usage" value={totals.solarToSiteKWh} suffix="kWh" />
          <Divider type="vertical" />
          <Tooltip title="Average transformer loading for site grid meters">
            <SummaryBox
              title={<span>Transformer Load <InfoCircleOutlined /></span>}
              value={totals.avgTransformerLoading == null ? null : totals.avgTransformerLoading * 100}
              suffix="%"
            />
          </Tooltip>
        </div>
        <div className="chartBody">
          {chartOption ? (
            <ReactEcharts
              option={chartOption}
              style={{ height: 480, width: '100%' }}
              notMerge
              showLoading={loading}
              loadingOption={loadingOption}
            />
          ) : loading ? (
            <ReactEcharts
              option={emptyOption}
              style={{ height: 480, width: '100%' }}
              notMerge
              showLoading
              loadingOption={loadingOption}
            />
          ) : (
            <Empty description="No readings for this date" />
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageChart;
